import { useResetCategoryAutoSelect } from 'hooks/useResetCategoryAutoSelect';
import { HeaderContext } from 'providers/HeaderProvider';
import { useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import IntegrationCommunication from 'services/integration-communication';
import { useAppSelector } from 'store/hooks';
import { isInPopUpSelector, paymentFlowSelector } from 'store/slices/global';
import { internalNavigateBack } from 'utils/navigationControl';

// Module-level stable event objects (Cyrillic 'С' is intentional — matches existing codebase)
const closeClickstreamEvent = new Event('СlickstreamClose');
const backClickstreamEvent = new Event('СlickstreamBack');

const clickstream = getEventAnalytic();

export function useHeaderNavigation() {
  const context = useContext(HeaderContext);

  if (!context) {
    throw new Error('useHeaderNavigation must be used within HeaderProvider');
  }

  const { config } = context;
  const configRef = useRef(config);
  configRef.current = config;

  const flowType = useAppSelector(paymentFlowSelector);
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);
  const navigate = useNavigate();
  const resetCategoryAutoSelect = useResetCategoryAutoSelect();

  const handleNavClick = useCallback(() => {
    const currentConfig = configRef.current;

    if (currentConfig.navIcon === 'close') {
      const event = getClickStreamEvent.cashier_homepage_close_click;
      clickstream.push(event, [cashierContext]);
      document.dispatchEvent(closeClickstreamEvent);
    } else {
      document.dispatchEvent(backClickstreamEvent);
      resetCategoryAutoSelect();
    }

    if (isOpenedInPopup && currentConfig.navIcon === 'close') {
      IntegrationCommunication.sendMessage({
        id: 'closeCashier',
      });
    } else if (currentConfig.onNavigationAction) {
      currentConfig.onNavigationAction();
    } else {
      internalNavigateBack(navigate);
    }
  }, [cashierContext, isOpenedInPopup, navigate, resetCategoryAutoSelect]);

  const handleCloseClick = useCallback(() => {
    const currentConfig = configRef.current;

    if (!currentConfig.onCloseAction) {
      handleNavClick();
      return;
    }

    const event = getClickStreamEvent.cashier_homepage_close_click;
    clickstream.push(event, [cashierContext]);
    document.dispatchEvent(closeClickstreamEvent);

    currentConfig.onCloseAction();
  }, [cashierContext, handleNavClick]);

  return {
    navIcon: config.navIcon,
    handleNavClick,
    handleCloseClick,
  };
}
